// ════════════════════════════════════════════════════════════════════════
//  Prüfung eines Entwurfs
//
//  Dieselben Funktionen, die auch die Lern-App benutzt – eine Wahrheitsquelle.
//  Hier nur zusammengefasst und um den Vergleich gegen die veröffentlichte
//  Fassung ergänzt.
// ════════════════════════════════════════════════════════════════════════

import { checkBundle, type Bundle, type Problem } from "@otto/lehrgang";
import { checkEditorial } from "./editorialChecks.js";
import { checkForeignKeys } from "./foreignKeys.js";
import { keysKeptProblems } from "./keysKept.js";

export type { Problem };

/**
 * Alle Probleme eines Entwurfs – inhaltlich, gegen die letzte Fassung, gegen
 * stehengebliebene Platzhalter und gegen die Schlüssel der anderen Lehrgänge.
 *
 * Die letzten beiden Prüfungen kennt nur die Admin-App. Unfertige Redaktion ist
 * kein ungültiges Bundle, aber ein Grund, nicht zu veröffentlichen (siehe
 * `editorialChecks.ts`); und was in anderen Lehrgängen steht, weiß
 * `packages/lehrgang` nicht – es prüft ein Bundle für sich.
 *
 * @param foreign Schlüssel aller ANDEREN Lehrgänge. Leer, solange sie noch nicht
 *                geladen sind: eine Prüfung ohne Datenlage darf nichts behaupten.
 * @param inUse   Veröffentlichte Schlüssel, an denen ein Ergebnis hängt. Nur die
 *                sind gegen Entfernen geschützt – was nie geübt wurde, darf
 *                wieder heraus.
 */
export function validate(
  bundle: Bundle,
  publishing: Bundle | null,
  foreign: ReadonlySet<string> = new Set(),
  inUse: ReadonlySet<string> = new Set(),
): readonly Problem[] {
  return [
    ...checkBundle(bundle),
    ...(publishing ? keysKeptProblems(publishing, bundle, inUse) : []),
    ...checkEditorial(bundle),
    ...checkForeignKeys(bundle, foreign),
  ];
}

/**
 * Probleme, die zu einem Pfad gehören – für die Anzeige an der Stelle.
 *
 * Der Vergleich endet an einer Pfadgrenze und nicht an einem beliebigen
 * Zeichen. Sonst zählte `lessons.grundreihe-4a` die Hinweise von
 * `lessons.grundreihe-4a-2` mit – und solche Schlüssel entstehen wirklich, der
 * Assistent hängt bei einer Kollision genau so einen Zähler an.
 */
export function problemsAt(
  problems: readonly Problem[],
  pathPrefix: string,
): readonly Problem[] {
  return problems.filter((p) => under(p.path, pathPrefix));
}

/** Dieselbe Auswahl, aber ohne die genannten Teilbäume – etwa ohne die Stufen. */
export function problemsAtExcept(
  problems: readonly Problem[],
  pathPrefix: string,
  excluded: readonly string[],
): readonly Problem[] {
  return problemsAt(problems, pathPrefix).filter(
    (p) => !excluded.some((prefix) => under(p.path, prefix)),
  );
}

function under(path: string, prefix: string): boolean {
  return path === prefix || path.startsWith(`${prefix}.`);
}
